import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Trash2, Phone, Megaphone, Calendar, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAD_SOURCES = [
  'Facebook', 'Instagram', 'Google Ads', 'Website', 
  'Email Campaign', 'WhatsApp', 'Referral', 'Trade Show', 'Field Marketing', 'Other'
];

export const MarketingLeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lead, setLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchLead();
    fetchUsers();
    fetchBranches();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API}/marketing-leads/${id}`);
      setLead(response.data);
    } catch (error) {
      toast.error('Failed to load marketing lead');
      navigate('/marketing-leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`);
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to load branches');
    }
  };

  // Filter users based on selected branch
  const filteredUsers = useMemo(() => {
    if (!lead?.branch_id) {
      return users; // Show all users if no branch selected
    }
    // Filter to show only users from the selected branch, plus admins (who have no branch restriction)
    return users.filter(u => u.branch_id === lead.branch_id || u.role === 'admin' || !u.branch_id);
  }, [users, lead?.branch_id]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (saving) return;
    
    setSaving(true);
    try {
      const updateData = {
        campaign_name: lead.campaign_name,
        source: lead.source,
        product_interest: lead.product_interest,
        estimated_value: lead.estimated_value || null,
        actual_value: lead.actual_value || null,
        priority: lead.priority,
        status: lead.status,
        assigned_to: lead.assigned_to || null,
        follow_up_date: lead.follow_up_date || null,
        notes: lead.notes || null
      };
      
      if (isAdmin) {
        updateData.branch_id = lead.branch_id || null;
      }
      
      await axios.put(`${API}/marketing-leads/${id}`, updateData);
      toast.success('Marketing lead updated successfully');
      fetchLead();
    } catch (error) {
      toast.error('Failed to update marketing lead');
    } finally {
      setSaving(false);
    }
  };

  // Handle branch change - clear assigned_to if user doesn't belong to new branch
  const handleBranchChange = (branchId) => {
    const newBranchId = branchId === 'unassigned' ? null : branchId;
    let newAssignedTo = lead.assigned_to;
    
    if (lead.assigned_to && newBranchId) {
      const assignedUser = users.find(u => u.id === lead.assigned_to);
      if (assignedUser && assignedUser.branch_id && assignedUser.branch_id !== newBranchId && assignedUser.role !== 'admin') {
        newAssignedTo = null;
        toast.info('Assigned user cleared as they do not belong to the new branch');
      }
    }
    
    setLead({ ...lead, branch_id: newBranchId, assigned_to: newAssignedTo });
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this marketing lead?')) return;
    if (deleting) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/marketing-leads/${id}`);
      toast.success('Marketing lead deleted successfully');
      navigate('/marketing-leads');
    } catch (error) {
      toast.error('Failed to delete marketing lead');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-purple-100 text-purple-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8" data-testid="marketing-lead-detail-page">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Link to="/marketing-leads">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Megaphone className="h-6 w-6 text-purple-600" />
                    {lead?.campaign_name}
                  </h1>
                  <p className="text-slate-500">Marketing Lead Details</p>
                </div>
              </div>
              {isAdmin && (
                <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="delete-marketing-lead-button">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Lead Info Card */}
              <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Lead Information</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(lead?.status)}`}>
                      {lead?.status}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getPriorityColor(lead?.priority)}`}>
                      {lead?.priority}
                    </span>
                  </div>
                  
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Customer</p>
                    <div className="flex items-center gap-2">
                      <p className="text-slate-900 font-medium">{lead?.customer_name}</p>
                      {lead?.customer_phone && (
                        <a href={`tel:${lead.customer_phone}`} className="flex items-center gap-1 text-base font-semibold text-blue-600 hover:text-blue-800">
                          <Phone className="h-4 w-4" />
                          {lead.customer_phone}
                        </a>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Source</p>
                    <p className="text-slate-900">{lead?.source}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Product Interest</p>
                    <p className="text-slate-900">{lead?.product_interest}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Estimated Value</p>
                      <p className="text-slate-900 font-medium">
                        {lead?.estimated_value ? `₹${lead.estimated_value.toLocaleString()}` : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Actual Value</p>
                      <p className="text-green-600 font-medium">
                        {lead?.actual_value ? `₹${lead.actual_value.toLocaleString()}` : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {lead?.follow_up_date && (
                    <div className="flex items-center gap-2 text-orange-600">
                      <Calendar className="h-4 w-4" />
                      <span>Follow up: {new Date(lead.follow_up_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {lead?.branch_name && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="h-4 w-4" />
                      <span>{lead.branch_name}</span>
                    </div>
                  )}
                  
                  {lead?.assigned_to_name && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="h-4 w-4" />
                      <span>Assigned: {lead.assigned_to_name}</span>
                    </div>
                  )}
                  
                  {lead?.created_by_name && (
                    <div className="text-sm text-slate-500">
                      Created by {lead.created_by_name}
                    </div>
                  )}
                  
                  <div className="text-sm text-slate-500">
                    Created: {new Date(lead?.created_at).toLocaleDateString()}
                  </div>
                  
                  {lead?.closed_at && (
                    <div className="text-sm font-medium text-green-600">
                      Closed: {new Date(lead.closed_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Edit Form */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Edit Lead</h2>
                <form onSubmit={handleUpdate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="campaign_name">Campaign Name</Label>
                      <Input
                        id="campaign_name"
                        value={lead?.campaign_name || ''}
                        onChange={(e) => setLead({ ...lead, campaign_name: e.target.value })}
                        data-testid="edit-campaign-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="source">Source</Label>
                      <Select value={lead?.source || ''} onValueChange={(value) => setLead({ ...lead, source: value })}>
                        <SelectTrigger data-testid="edit-source-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_SOURCES.map((source) => (
                            <SelectItem key={source} value={source}>{source}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="product_interest">Product Interest</Label>
                    <Input
                      id="product_interest"
                      value={lead?.product_interest || ''}
                      onChange={(e) => setLead({ ...lead, product_interest: e.target.value })}
                      data-testid="edit-product-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select value={lead?.status || ''} onValueChange={(value) => setLead({ ...lead, status: value })}>
                        <SelectTrigger data-testid="edit-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="won">Won</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <Select value={lead?.priority || ''} onValueChange={(value) => setLead({ ...lead, priority: value })}>
                        <SelectTrigger data-testid="edit-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="estimated_value">Estimated Value (₹)</Label>
                      <Input
                        id="estimated_value"
                        type="number"
                        value={lead?.estimated_value || ''}
                        onChange={(e) => setLead({ ...lead, estimated_value: e.target.value ? parseFloat(e.target.value) : null })}
                        data-testid="edit-estimated-value-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="actual_value">Actual Value (₹)</Label>
                      <Input
                        id="actual_value"
                        type="number"
                        value={lead?.actual_value || ''}
                        onChange={(e) => setLead({ ...lead, actual_value: e.target.value ? parseFloat(e.target.value) : null })}
                        data-testid="edit-actual-value-input"
                      />
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <div className="space-y-2">
                      <Label htmlFor="branch">Branch</Label>
                      <Select value={lead?.branch_id || 'unassigned'} onValueChange={handleBranchChange}>
                        <SelectTrigger data-testid="edit-branch-select">
                          <SelectValue placeholder="Select branch" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {branches.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="assigned_to">Assigned To</Label>
                      <Select value={lead?.assigned_to || 'unassigned'} onValueChange={(value) => setLead({ ...lead, assigned_to: value === 'unassigned' ? null : value })}>
                        <SelectTrigger data-testid="edit-assigned-to-select">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {filteredUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} {u.branch_id && lead?.branch_id && u.branch_id !== lead.branch_id ? '(Other Branch)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {lead?.branch_id && (
                        <p className="text-xs text-slate-500">Showing members from selected branch</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="follow_up_date">Follow Up Date</Label>
                      <Input
                        id="follow_up_date"
                        type="date"
                        value={lead?.follow_up_date || ''}
                        onChange={(e) => setLead({ ...lead, follow_up_date: e.target.value })}
                        data-testid="edit-follow-up-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={lead?.notes || ''}
                      onChange={(e) => setLead({ ...lead, notes: e.target.value })}
                      rows={4}
                      data-testid="edit-notes-input"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={saving} data-testid="save-marketing-lead-button">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
